/**
 * Session setup — the screen that runs the moment a game ends.
 *
 * Everything here is arranged around one fact: this screen is used in a loud
 * room, on a phone, on somebody else's wifi, by a person who would rather be
 * arguing about the game than operating an app. Three consequences run through
 * the file:
 *
 *  1. **Nothing waits on BGG.** The local library is searched first and always,
 *     manual entry is a permanent visible option rather than an error path, and
 *     a failing BGG proxy demotes itself to a one-line notice. A group must
 *     never be unable to rate the game they just played.
 *  2. **"Same as last time" is the fast path.** A group that already decided
 *     which axes matter for Brass should not re-decide every fortnight, so the
 *     recalled config sits directly under the game, before the two sections it
 *     makes unnecessary.
 *  3. **Rows are created as they are typed, not at submit.** A guest chip
 *     becomes a real `profile` row and starts syncing the second it is added,
 *     because `startSession` is a server call and the server can only accept
 *     profile and game ids it already has. Doing that work at the last tap
 *     would put the whole round-trip between "Start rating" and the screen
 *     changing.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";

import { navigate } from "../../main.tsx";
import type { ScreenProps } from "../../main.tsx";
import { Tile } from "../../components/Tile.tsx";
import { BoxArt } from "../catalog/components.tsx";
import type { ArtSubject } from "../catalog/components.tsx";
import { ulid } from "../../../shared/ids.ts";
import { normalise } from "../../../shared/title-key.ts";
import type { Aspect, CaptureMode, Game, Profile, SubQuestion } from "../../../shared/types.ts";
import { coPlayerStandings, mutate } from "../../store/outbox.ts";
import type { CoPlayerStanding } from "../../store/outbox.ts";
import { adoptServerRow, pendingCount, rollbackCreate, syncNow } from "../../store/sync.ts";
import { defaultColour } from "../settings/logic.ts";
import { QrCanvas } from "./QrCanvas.tsx";
import {
  ApiError,
  fetchStableAnswers,
  newParticipantId,
  openJoin,
  searchGames,
  startSession,
} from "./api.ts";
import type { BggHit, JoinOffer, SessionGraph, StartSessionInput } from "./api.ts";
import {
  activeAspects,
  activeSubQuestionsByAspect,
  defaultAspectIds,
  loadSetupData,
  orderRoster,
  recallForGame,
  searchLocalGames,
} from "./local.ts";
import type { Recall, SetupData } from "./local.ts";

const EMPTY_DATA: SetupData = {
  profiles: [],
  aspects: [],
  games: [],
  sessions: [],
  participants: [],
  sessionAspects: [],
  subquestions: [],
};

/** Long enough that a title typed at speed is one request, short enough to feel live. */
const SEARCH_DEBOUNCE_MS = 320;

type GameChoice =
  | { kind: "local"; game: Game }
  | { kind: "bgg"; hit: BggHit }
  | { kind: "manual"; title: string };

type BggState = "idle" | "searching" | "ok" | "down";

const MODES: readonly { value: CaptureMode; title: string; blurb: string }[] = [
  {
    value: "SHARED_DEVICE",
    title: "One phone, passed round",
    blurb:
      "This phone goes round the table. Each person taps their own score and hands it on. Nobody sees anyone else's until everyone is in.",
  },
  {
    value: "MULTI_DEVICE",
    title: "Everyone on their own phone",
    blurb:
      "You get a code to share. Each player joins from their own phone and rates at their own pace — same rule, still hidden until everyone has answered.",
  },
];

function choiceTitle(choice: GameChoice): string {
  if (choice.kind === "local") return choice.game.title;
  if (choice.kind === "bgg") return choice.hit.title;
  return choice.title;
}

function choiceYear(choice: GameChoice): number | null {
  if (choice.kind === "local") return choice.game.year;
  if (choice.kind === "bgg") return choice.hit.year;
  return null;
}

/**
 * The picked game as something `BoxArt` can render.
 *
 * A manually typed title has no art and never will, which is a real answer:
 * the punched blank with its initials is what a game we know nothing about
 * looks like everywhere else in the app.
 */
function choiceArt(choice: GameChoice): ArtSubject {
  if (choice.kind === "local") return choice.game;
  if (choice.kind === "bgg") {
    return { title: choice.hit.title, thumb_url: choice.hit.thumbUrl, image_url: null };
  }
  return { title: choice.title, thumb_url: null, image_url: null };
}

function shortDate(at: number): string {
  return new Date(at).toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

function clockTime(at: number): string {
  return new Date(at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

function sameMembers(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  return b.every((id) => set.has(id));
}

/**
 * The line under the aspect chips.
 *
 * The first two sentences are true of every night, so they are always said. The
 * third one is not: it explains the `+6 follow-ups` badge a chip wears, and it
 * was on screen whether or not a single chip wore one — a hint that is always up
 * is a hint nobody is still reading by the evening it finally matters. The
 * seeded taxonomy now puts a badge on every chip, so on most tables it does
 * appear; a group that turned the six off, or built their own, still gets the
 * line the moment the first badge is back.
 *
 * What it says had to change with the detail branch. The follow-ups no longer
 * follow the aspect — they are folded behind "Go into detail" and a rater who
 * puts a number on the topic never meets them — so the old sentence promised a
 * screen nobody was going to see, on the one screen a group reads carefully.
 *
 * Keyed on the chips being *offered* rather than on the ones switched on. The
 * badge is on the chip either way, so that is when the sentence has something to
 * explain; keying it to the selection would make a line appear and disappear
 * under the group's thumb as they toggle chips, which is its own distraction on
 * the one screen used with friends waiting.
 */
export function aspectHint(
  aspects: readonly { id: string }[],
  followUps: ReadonlyMap<string, readonly SubQuestion[]>,
): string {
  const base =
    "Only the 1–10 score is required. Everything here can be skipped, or turned off now to keep it short.";
  const anyFollowUps = aspects.some((aspect) => (followUps.get(aspect.id)?.length ?? 0) > 0);
  return anyFollowUps
    ? `${base} An aspect's follow-up questions stay folded away behind "Go into detail" — rate the topic itself and you never see them.`
    : base;
}

/**
 * The chips to start a night with, once the game's settled answers are known.
 *
 * An aspect marked "once per game" is a question about the box rather than
 * about the evening — how it looks, how well the rules are written — so a group
 * that has already answered it for this game should not be asked it again on
 * the next night. Until now the chip said "once per game" and the night asked
 * anyway, which is the defect this closes.
 *
 * It only ever takes chips off. Turning one back on is the host's decision and
 * this runs after every answer the server sends, so re-adding here would quietly
 * undo a deliberate tap; and nothing is hidden either way — the chip stays on
 * screen, wearing the date it was answered, one tap from being asked again.
 */
export function unaskedAspects(
  chosen: ReadonlySet<string>,
  answered: Iterable<string>,
): ReadonlySet<string> {
  const next = new Set(chosen);
  for (const id of answered) next.delete(id);
  return next;
}

// ---------------------------------------------------------------------------
// Starting the night
// ---------------------------------------------------------------------------

/** Why a tap on "Start rating" did not become a game night. */
export type StartRefusal = "offline" | "unsent" | "unknown-rows" | "server";

export type StartResult =
  | { kind: "started"; graph: SessionGraph }
  /** `keptGame`: the row this tap minted is still on the shelf. See `undo`. */
  | { kind: "refused"; refusal: StartRefusal; keptGame: boolean };

/** Injected so the ordering can be tested without a network or a store. */
export interface StartDeps {
  online: () => boolean;
  /** The picked game as an id the server will accept; `created` if it is new. */
  resolveGame: () => Promise<{ id: string; created: boolean }>;
  drain: () => Promise<void>;
  queued: () => Promise<number>;
  create: (input: StartSessionInput) => Promise<SessionGraph>;
  /** Take the minted game back. `false` means it is still there — say so. */
  undoGame: (id: string) => Promise<boolean>;
  adopt: (graph: SessionGraph) => Promise<void>;
}

/**
 * What the table is told, in the table's language.
 *
 * Written here rather than passed up from the transport on purpose: `fetch`
 * rejects with "Load failed" and HTTP/2 carries no reason phrase, so the raw
 * message for a 500 is the three characters "500". Neither tells anyone at the
 * table what to do next, which is the only thing a notice is for. Every entry
 * says what happened and what the next tap is.
 */
const REFUSALS: Record<StartRefusal, { title: string; body: string }> = {
  offline: {
    title: "No signal",
    body: "Starting a night needs the network for a moment — it sets up everyone's seats. Find a signal and tap again. Once it has started, the rating itself works offline.",
  },
  unsent: {
    title: "Catching up",
    body: "Some of tonight's setup is still waiting to be sent. Nothing is lost — give it a moment on a good signal and tap again.",
  },
  "unknown-rows": {
    title: "Not caught up",
    body: "This device knows about a game or a player that hasn't reached the server yet. Wait for the sync to finish, then tap again.",
  },
  server: {
    title: "Nothing happened",
    body: "The night did not start. Nothing has been saved and nothing is lost — tap again in a moment.",
  },
};

/**
 * What is said instead when the game could not be taken back off the shelf.
 *
 * Every entry above ends in some version of "nothing is lost", and for the
 * server case it said so outright — which was true right up until the tombstone
 * was refused and the game stayed on the shelf of every other device. The notice
 * cannot promise a clean shelf it did not clean, so when the rollback reports
 * `kept` it says what is there and leaves the next tap unchanged. No version
 * number, no word for the machinery: the row is on the shelf and can be deleted
 * from it like any other, which is the whole of what the table can act on.
 */
const KEPT_GAME = {
  title: "The night did not start",
  body: "The game stayed on your shelf, but the night itself did not start — tap again in a moment. If you would rather not keep the game, delete it from the shelf.",
};

export function refusalText(result: { refusal: StartRefusal; keptGame?: boolean }): {
  title: string;
  body: string;
} {
  if (result.keptGame) return KEPT_GAME;
  return REFUSALS[result.refusal];
}

/**
 * Whether a failed attempt may take its game row back off the shelf.
 *
 * The rule is narrow by design. A tap that fails should leave no trace, but a
 * game the group typed in last week — or played last week — is not this tap's
 * to delete, and QA's phantom rows are only the ones this attempt minted.
 */
export function undoableGame(
  minted: { id: string; created: boolean },
  sessions: readonly { deleted: 0 | 1; game_id: string }[],
): boolean {
  if (!minted.created) return false;
  return !sessions.some((s) => s.deleted !== 1 && s.game_id === minted.id);
}

/**
 * The account holder's own profile, or null if the roster does not hold one.
 *
 * Nothing in the model marks the owner, and the roster's own order used to stand
 * in for it — the top of a recency-weighted list is "whoever's phone this is",
 * which is true for exactly one night. From the second night on, anyone who
 * plays every night the owner does ties with them precisely (same plays, same
 * recency, same score) and `coPlayerStandings` breaks the tie on name, so a
 * partner called Adi took the top slot and the owner had to add themselves to
 * their own game night by hand, every time.
 *
 * Identified as the oldest non-guest profile instead: provisioning writes the
 * account holder's profile first (`ensureSelfProfile` in `src/server/seed.ts`)
 * and ULIDs sort by creation time, so the oldest row is theirs and stays theirs
 * however they rename it. Guests are excluded at any age — somebody added at the
 * table is somebody else at the table.
 *
 * Matching on the signed-in user's *name* was the other option and is worse: it
 * is the server's own match, and it breaks the moment the owner renames their
 * profile, which is a thing the settings screen invites them to do.
 */
export function accountProfile(roster: readonly Profile[]): Profile | null {
  let oldest: Profile | null = null;
  for (const profile of roster) {
    if (profile.is_guest === 1 || profile.deleted === 1 || profile.archived_at) continue;
    if (!oldest || profile.id < oldest.id) oldest = profile;
  }
  return oldest;
}

/**
 * Land the night the server just minted, without waiting for a pull.
 *
 * Same reasoning as the completion path in `api.ts`: the shelf, the catalog and
 * the game page all read the *local* store, so a session that exists only on the
 * server is one the group cannot back out of and come back to. Row by row rather
 * than in one write because `adoptServerRow` is the only landing the store
 * offers, and a handful of extra notifications on the one tap of the evening is
 * cheaper than a second copy of the pull's landing logic.
 */
async function adoptGraph(graph: SessionGraph): Promise<void> {
  await adoptServerRow("play_session", graph.session);
  for (const row of graph.participants) await adoptServerRow("session_participant", row);
  for (const row of graph.aspects) await adoptServerRow("session_aspect", row);
  for (const row of graph.subquestions) await adoptServerRow("session_subquestion", row);
}

/**
 * The whole of "Start rating", in the order the steps have to happen.
 *
 * Offline is refused rather than queued. Minting a night is the server's job:
 * it freezes the aspect and player snapshots and issues the join code in one
 * transaction, so a night forged on the device would have to guess at a shape
 * the server may not agree with — and a guess that turns out wrong dead-letters
 * the whole evening's scaffolding *after* the group has rated into it. Being
 * told up front costs one sentence; being told at the end costs the night. The
 * offline promise is about ratings, and those still queue: it is only the first
 * tap that needs a signal.
 *
 * The refusal is returned rather than thrown so the caller cannot accidentally
 * swallow it — that silence is the bug this function exists to close.
 */
export async function startNight(
  build: (gameId: string) => StartSessionInput,
  deps: StartDeps,
): Promise<StartResult> {
  // Before `resolveGame`, so an offline tap never mints a game row at all.
  // Rolling one back is a second chance to leave a phantom on the shelf.
  if (!deps.online()) return { kind: "refused", refusal: "offline", keptGame: false };

  const game = await deps.resolveGame();
  /**
   * A refusal, plus whether the shelf is actually clean.
   *
   * The rollback can genuinely fail — another device may have touched the row,
   * or this one may not reach the server at all — and it used to fail in
   * silence: `undoGame` returned nothing, so a refused tombstone and a
   * successful one produced the same sentence. A throw is treated as a failure
   * for the same reason; the tap's own refusal is what the table needs to hear,
   * but not at the price of claiming a shelf nobody cleaned.
   */
  const undo = async (refusal: StartRefusal): Promise<StartResult> => {
    if (!game.created) return { kind: "refused", refusal, keptGame: false };
    const removed = await deps.undoGame(game.id).catch(() => false);
    return { kind: "refused", refusal, keptGame: !removed };
  };

  await deps.drain();
  // The server would answer 404 for rows it has not been sent yet, which is a
  // worse sentence for the same fact. Ask the outbox instead.
  if ((await deps.queued()) > 0) return undo("unsent");

  let graph: SessionGraph;
  try {
    graph = await deps.create(build(game.id));
  } catch (err) {
    return undo(err instanceof ApiError && err.status === 404 ? "unknown-rows" : "server");
  }

  // The server has the night either way. A local landing that fails leaves a
  // stale shelf the next pull repairs — not a reason to tell the table their
  // night never started.
  await deps.adopt(graph).catch(() => undefined);
  return { kind: "started", graph };
}

export function Setup({ query }: ScreenProps): ReactNode {
  const preselectedGame = query.get("game");

  const [data, setData] = useState<SetupData | null>(null);
  const [standings, setStandings] = useState<CoPlayerStanding[]>([]);
  const [text, setText] = useState("");
  const [choice, setChoice] = useState<GameChoice | null>(null);
  const [hits, setHits] = useState<BggHit[]>([]);
  const [bgg, setBgg] = useState<BggState>("idle");
  const [chosenProfiles, setChosenProfiles] = useState<string[]>([]);
  const [chosenAspects, setChosenAspects] = useState<ReadonlySet<string>>(new Set());
  // The once-per-game aspects this game has already been rated on, each with the
  // night that answered it. Empty until a game from the library is picked: a
  // game being typed in for the first time has no history to have settled.
  const [answeredStable, setAnsweredStable] = useState<ReadonlyMap<string, number>>(new Map());
  const [guestName, setGuestName] = useState("");
  const [mode, setMode] = useState<CaptureMode>("SHARED_DEVICE");
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<{
    refusal: StartRefusal;
    keptGame?: boolean;
  } | null>(null);

  // Set once the session exists on the server. Kept separately from `offer` so a
  // failed join never loses a session that was created successfully.
  const [createdId, setCreatedId] = useState<string | null>(null);
  const [offer, setOffer] = useState<JoinOffer | null>(null);
  const [qrOk, setQrOk] = useState(true);
  const [copied, setCopied] = useState(false);

  // A retried "Start rating" must reuse the id, or the first attempt's session
  // survives as an orphan draft. This is what `StartSessionInput.id` is for.
  const sessionIdRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([loadSetupData(), coPlayerStandings()])
      .then(([loaded, ranking]) => {
        if (cancelled) return;
        setData(loaded);
        setStandings(ranking);
        const ranked = orderRoster(loaded.profiles, ranking);
        // The account holder is in every session by definition, so they are
        // offered without a tap — and offered as *themselves*, not as whoever
        // the roster happens to rank first. Removable like anyone else. The top
        // of the roster is the fallback for a device whose only profiles are
        // guests, where there is no owner row to find.
        const first = accountProfile(ranked) ?? ranked[0];
        setChosenProfiles(first ? [first.id] : []);
        setChosenAspects(new Set(defaultAspectIds(loaded.aspects)));
      })
      .catch(() => {
        if (!cancelled) setData(EMPTY_DATA);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Arriving from a game page: that game is the answer, so skip the search.
  useEffect(() => {
    if (!data || !preselectedGame || choice) return;
    const game = data.games.find((g) => g.deleted !== 1 && g.id === preselectedGame);
    if (game) setChoice({ kind: "local", game });
  }, [data, preselectedGame, choice]);

  /**
   * Ask the server which once-per-game aspects this game has already settled.
   *
   * It waits for `data`, because the load above seeds the default chips and this
   * takes chips away from that seed — arriving first would only be overwritten.
   *
   * The answer has to come from the server. On a night played over QR codes every
   * other phone posted its ratings straight to the server, so this device's own
   * store holds one seat's worth of an evening the whole table rated. Setup
   * already refuses to start a night offline, so needing the network here costs
   * nothing that was not already required.
   *
   * A failure is silence rather than a message. The worst case is the old
   * behaviour — a settled question asked one more time — and the person holding
   * the phone has friends waiting and can untick the chip themselves; an error
   * about a request they did not make would be worse than the thing it reports.
   */
  useEffect(() => {
    if (!data || choice?.kind !== "local") {
      setAnsweredStable(new Map());
      return;
    }
    let cancelled = false;
    const gameId = choice.game.id;
    fetchStableAnswers(gameId)
      .then((rows) => {
        if (cancelled) return;
        setAnsweredStable(new Map(rows.map((row) => [row.aspect_id, row.played_at] as const)));
        setChosenAspects((current) =>
          unaskedAspects(
            current,
            rows.map((row) => row.aspect_id),
          ),
        );
      })
      .catch(() => {
        if (!cancelled) setAnsweredStable(new Map());
      });
    return () => {
      cancelled = true;
    };
  }, [data, choice]);

  const localHits = useMemo(
    () => (data ? searchLocalGames(data.games, text) : []),
    [data, text],
  );

  const aspects: Aspect[] = useMemo(() => (data ? activeAspects(data.aspects) : []), [data]);

  // Choosing an aspect chooses its follow-ups — the server derives them from
  // the aspect ids this screen sends, so there is no second picker. The count
  // on the chip is what keeps that honest: the group sees how many extra
  // questions an aspect brings before they turn it on, rather than meeting them
  // mid-night.
  const followUps = useMemo<Map<string, SubQuestion[]>>(
    () => (data ? activeSubQuestionsByAspect(data.subquestions) : new Map()),
    [data],
  );

  // Counted over the chips actually on screen rather than over the server's
  // answer, because an aspect can have been answered on an earlier night and
  // since been archived in settings — it has no chip, so a line explaining why
  // its chip starts off would be explaining nothing.
  const settledOffered = useMemo(
    () => aspects.filter((aspect) => answeredStable.has(aspect.id)).length,
    [aspects, answeredStable],
  );

  const roster: Profile[] = useMemo(
    () => (data ? orderRoster(data.profiles, standings) : []),
    [data, standings],
  );

  /** "3 plays · last 12 Jul" under a name, straight off the shared standings. */
  const standingOf = useMemo(
    () => new Map(standings.map((s) => [s.profile_id, s] as const)),
    [standings],
  );

  useEffect(() => {
    const q = text.trim();
    if (choice || q.length < 2) {
      setHits([]);
      setBgg("idle");
      return;
    }
    const controller = new AbortController();
    setBgg("searching");
    const timer = window.setTimeout(() => {
      searchGames(q, controller.signal)
        .then((rows) => {
          if (controller.signal.aborted) return;
          setHits(rows);
          setBgg("ok");
        })
        .catch(() => {
          if (controller.signal.aborted) return;
          setHits([]);
          // Every BGG failure — no token yet, rate limit, dead wifi — has the
          // same answer for the person holding the phone, so they all collapse
          // into one state rather than a taxonomy of excuses.
          setBgg("down");
        });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [text, choice]);

  /**
   * Remote hits, minus the ones already offered above as library rows.
   *
   * Deliberately keyed on what is *on screen* rather than on the whole library.
   * Filtering against every game owned meant a hit could be dropped as "already
   * yours" while the local list had not surfaced it — and then the only thing
   * left for a game the group had played repeatedly was the type-it-in row. The
   * two searches are not the same algorithm (this one ranks by match position
   * and stops at eight; the bundled catalogue tiers exact/prefix/substring and
   * stops at ten), so a library where nine titles match the query swallowed
   * remote answers for everything past the eighth.
   *
   * Scoping the dedupe to the rendered rows makes that impossible by
   * construction: anything the local list did not show survives as a remote row,
   * whatever the two rankers disagree about. A duplicate can only appear next to
   * the row it duplicates, where it is obvious rather than invisible.
   */
  const shownBggIds = useMemo(() => {
    const ids = new Set<number>();
    for (const g of localHits) {
      if (g.bgg_id !== null) ids.add(g.bgg_id);
    }
    return ids;
  }, [localHits]);

  const remoteHits = useMemo(
    () => hits.filter((hit) => !shownBggIds.has(hit.bggId)),
    [hits, shownBggIds],
  );

  const recall: Recall | null = useMemo(() => {
    if (!data || choice?.kind !== "local") return null;
    const live = new Set(activeAspects(data.aspects).map((a) => a.id));
    const found = recallForGame(choice.game.id, data, live);
    if (!found) return null;
    // The roster's own predicate, not just "not tombstoned": an archived player
    // renders no chip, so recalling them would put a seat at the table that
    // nobody can rate through and the reveal gate would wait on them all night.
    const known = new Set(orderRoster(data.profiles, []).map((p) => p.id));
    const profile_ids = found.profile_ids.filter((id) => known.has(id));
    if (profile_ids.length === 0 && found.aspect_ids.length === 0) return null;
    return { ...found, profile_ids };
  }, [data, choice]);

  const recallApplied =
    recall !== null &&
    sameMembers(recall.profile_ids, chosenProfiles) &&
    sameMembers(recall.aspect_ids, [...chosenAspects]);

  function applyRecall(): void {
    if (!recall) return;
    setChosenProfiles(recall.profile_ids);
    setChosenAspects(new Set(recall.aspect_ids));
  }

  function toggleProfile(id: string): void {
    setChosenProfiles((current) =>
      current.includes(id) ? current.filter((p) => p !== id) : [...current, id],
    );
  }

  function toggleAspect(id: string): void {
    setChosenAspects((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  /**
   * A guest becomes a real (guest-flagged) profile immediately.
   *
   * `startSession` validates every participant id against the server, so the
   * row has to exist and have replicated before the session can start. Writing
   * it on "Add" gives sync the whole rest of the setup to get it there.
   */
  async function addGuest(): Promise<void> {
    const name = guestName.trim();
    if (!name || !data) return;
    const id = newParticipantId();
    const colour = defaultColour(data.profiles);
    const now = Date.now();
    await mutate(
      "profile",
      id,
      { name, colour, avatar: null, depth_mode: "FULL", is_guest: 1, archived_at: null },
      0,
    );
    // `owner_id` and the sync counters are the server's to assign; the local row
    // genuinely does not have them until the next pull, and nothing on this
    // screen reads them.
    const guest: Profile = {
      id,
      owner_id: "",
      version: 0,
      updated_seq: 0,
      deleted: 0,
      updated_at: now,
      name,
      colour,
      avatar: null,
      depth_mode: "FULL",
      is_guest: 1,
      archived_at: null,
    };
    setData({ ...data, profiles: [...data.profiles, guest] });
    setChosenProfiles((current) => [...current, id]);
    setGuestName("");
    void syncNow();
  }

  /** Resolves the picked game to an id the server will accept, creating it if new. */
  async function resolveGameId(
    picked: GameChoice,
    loaded: SetupData,
  ): Promise<{ id: string; created: boolean }> {
    if (picked.kind === "local") return { id: picked.game.id, created: false };

    if (picked.kind === "bgg") {
      // `game(owner_id, bgg_id)` is unique, so a second row for the same BGG id
      // would be rejected on push rather than merged.
      const existing = loaded.games.find((g) => g.deleted !== 1 && g.bgg_id === picked.hit.bggId);
      if (existing) return { id: existing.id, created: false };
      const id = ulid();
      await mutate(
        "game",
        id,
        {
          bgg_id: picked.hit.bggId,
          title: picked.hit.title,
          year: picked.hit.year,
          // `image_url` stays null: the index carries only the 64x64 thumbnail,
          // and inventing a full-size URL from it is not possible — the path is
          // signed. A later `/thing` lookup fills this in if a token appears.
          image_url: null,
          thumb_url: picked.hit.thumbUrl,
          min_players: null,
          max_players: null,
          playtime: null,
          weight: null,
          manual: 0,
          cached_at: null,
        },
        0,
      );
      return { id, created: true };
    }

    const title = picked.title.trim();
    // Folded, not lower-cased. A group that types "brass birmingham" for a shelf
    // that holds "Brass: Birmingham" means the game they own, and a raw
    // comparison would mint a second row for it — the same punctuation blindness
    // that `searchLocalGames` had, in the branch that decides identity rather
    // than the one that decides display.
    const key = normalise(title);
    const existing = loaded.games.find((g) => g.deleted !== 1 && normalise(g.title) === key);
    if (existing) return { id: existing.id, created: false };
    const id = ulid();
    await mutate(
      "game",
      id,
      {
        bgg_id: null,
        title,
        year: null,
        image_url: null,
        thumb_url: null,
        min_players: null,
        max_players: null,
        playtime: null,
        weight: null,
        manual: 1,
        cached_at: null,
      },
      0,
    );
    return { id, created: true };
  }

  async function start(): Promise<void> {
    if (!data || !choice || chosenProfiles.length === 0) return;
    setBusy(true);
    setRefusal(null);
    try {
      let sessionId = createdId;

      if (!sessionId) {
        const order = new Map(aspects.map((a, index) => [a.id, index]));
        const result = await startNight(
          (gameId) => ({
            id: (sessionIdRef.current ??= ulid()),
            game_id: gameId,
            capture_mode: mode,
            participant_profile_ids: chosenProfiles,
            aspect_ids: [...chosenAspects].sort(
              (a, b) => (order.get(a) ?? 0) - (order.get(b) ?? 0),
            ),
            player_count: chosenProfiles.length,
            first_play: !data.sessions.some((s) => s.deleted !== 1 && s.game_id === gameId),
          }),
          {
            online: () => navigator.onLine !== false,
            resolveGame: () => resolveGameId(choice, data),
            drain: syncNow,
            queued: pendingCount,
            create: startSession,
            undoGame: async (id) => {
              // `startNight` has already established this row was minted by the
              // tap that just failed; this is the second half of the same rule,
              // and the half that reads the store the first half cannot see.
              //
              // Reported as removed when the rule says leave it: a game the
              // group already has a night on is not a leftover of this tap, and
              // saying otherwise would put the "still on your shelf" notice in
              // front of somebody whose shelf is exactly as they left it.
              if (!undoableGame({ id, created: true }, data.sessions)) return true;
              return (await rollbackCreate("game", id)) === "removed";
            },
            adopt: adoptGraph,
          },
        );
        if (result.kind === "refused") {
          setRefusal(result);
          return;
        }
        sessionId = result.graph.session.id;
      }

      if (mode === "SHARED_DEVICE") {
        navigate(`/session/${sessionId}`);
        return;
      }

      setCreatedId(sessionId);
      // A retried offer is a different symbol to encode and a different link to
      // copy, so neither verdict from the previous one carries over. A refused
      // offer is not a refused night: the session is saved either way, and the
      // "No code" notice below is what says so.
      setQrOk(true);
      setCopied(false);
      setOffer(await openJoin(sessionId).catch(() => null));
    } finally {
      setBusy(false);
    }
  }

  const joinUrl = offer ? `${window.location.origin}/#/join/${offer.join_code}` : "";

  const onEncoded = useCallback((ok: boolean) => setQrOk(ok), []);

  async function copyJoinLink(): Promise<void> {
    try {
      await navigator.clipboard?.writeText(joinUrl);
      setCopied(true);
    } catch {
      // No clipboard permission — the code is on screen in large type anyway,
      // which is what most people at a table read out loud regardless.
    }
  }

  // -------------------------------------------------------------------------
  // The join step
  // -------------------------------------------------------------------------

  if (createdId) {
    return (
      <div className="pb-screen">
        <div className="pb-screen__head">
          <h1 className="pb-screen__title">Everyone in</h1>
          <span className="pb-screen__sub">Step 2 of 2</span>
        </div>

        {offer ? (
          <div className="pb-join">
            {/* Kept mounted, not conditionally rendered: unmounting on a failed
                encode would also stop the encoder ever seeing the next code.
                Hidden rather than blank — an empty white square reads as broken. */}
            <div className="pb-qr-frame" style={{ display: qrOk ? undefined : "none" }}>
              <QrCanvas text={joinUrl} size={240} onEncoded={onEncoded} />
            </div>
            <div className="pb-code">{offer.join_code}</div>
            <div className="pb-code__hint">
              {qrOk ? "Scan it, or type the code" : "Type this code to join"}
            </div>
            <a className="pb-link" href={joinUrl} target="_blank" rel="noreferrer">
              {joinUrl}
            </a>
            <div className="pb-code__hint">Works until {clockTime(offer.join_expires_at)}</div>
          </div>
        ) : (
          <div className="pb-notice" data-tone="alert">
            <span className="pb-notice__strong">No code</span>
            <span>
              The session is saved, but the join code didn't come through. Try again, or rate on
              this phone and pass it round.
            </span>
          </div>
        )}

        <p className="pb-hint">
          Latecomers can still join with this code while you rate — nobody has to be here yet.
        </p>

        <div className="pb-actions">
          {offer ? (
            <button
              type="button"
              className="pb-btn"
              data-variant="quiet"
              onClick={() => void copyJoinLink()}
            >
              {copied ? "Copied" : "Copy link"}
            </button>
          ) : (
            <button
              type="button"
              className="pb-btn"
              data-variant="quiet"
              disabled={busy}
              onClick={() => void start()}
            >
              {busy ? "Trying" : "Try again"}
            </button>
          )}
          <button
            type="button"
            className="pb-btn"
            data-variant="primary"
            onClick={() => navigate(`/session/${createdId}`)}
          >
            Start rating
          </button>
        </div>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Setup
  // -------------------------------------------------------------------------

  const typed = text.trim();
  const canStart = Boolean(choice) && chosenProfiles.length > 0 && !busy;

  return (
    <div className="pb-screen">
      <div className="pb-screen__head">
        <h1 className="pb-screen__title">What did you play?</h1>
        <span className="pb-screen__sub">Step 1 of 2</span>
      </div>

      <div className="pb-section">
        {choice ? (
          <Tile edge="spice">
            <div className="pb-picked">
              <BoxArt game={choiceArt(choice)} size={72} eager />
              <div className="pb-picked__body">
                <div className="pb-tile__title">{choiceTitle(choice)}</div>
                <div className="pb-tile__meta">
                  {choice.kind === "manual"
                    ? "Typed in"
                    : [choiceYear(choice), choice.kind === "local" ? "In your library" : "From BGG"]
                        .filter(Boolean)
                        .join(" · ")}
                </div>
              </div>
            </div>

            <div className="pb-actions">
              <button
                type="button"
                className="pb-btn"
                data-variant="quiet"
                onClick={() => {
                  setChoice(null);
                  setText("");
                }}
              >
                Different game
              </button>
            </div>
          </Tile>
        ) : (
          <>
            <div className="pb-field">
              <label className="pb-label" htmlFor="pb-game-search">
                Game
              </label>
              <input
                id="pb-game-search"
                className="pb-input"
                type="search"
                autoComplete="off"
                value={text}
                placeholder="Start typing the title"
                onChange={(event) => setText(event.target.value)}
              />
            </div>

            {/* The server answers title searches from a catalogue bundled with
                the app, so this no longer fires for a missing BGG token or a
                rate limit — only when the lookup itself could not be reached at
                all. Rare enough to deserve a notice, and never a dead end. */}
            {bgg === "down" && (
              <div className="pb-notice" data-tone="wait">
                <span className="pb-notice__strong">Lookup quiet</span>
                <span>Your own library still works, and you can type the title in.</span>
              </div>
            )}

            {/* Art at 44px, the tap target size: big enough to recognise a box
                you own, small enough that ten results still fit on a phone. */}
            {localHits.map((game) => (
              <Tile
                key={game.id}
                as="button"
                type="button"
                edge="slate"
                onClick={() => setChoice({ kind: "local", game })}
              >
                <div className="pb-result">
                  <BoxArt game={game} size={44} />
                  <div className="pb-result__body">
                    <div className="pb-tile__title">{game.title}</div>
                    <div className="pb-tile__meta">
                      {[game.year, "In your library"].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                </div>
              </Tile>
            ))}

            {remoteHits.map((hit) => (
              <Tile
                key={hit.bggId}
                as="button"
                type="button"
                edge="muted"
                onClick={() => setChoice({ kind: "bgg", hit })}
              >
                <div className="pb-result">
                  <BoxArt
                    game={{ title: hit.title, thumb_url: hit.thumbUrl, image_url: null }}
                    size={44}
                  />
                  <div className="pb-result__body">
                    <div className="pb-tile__title">{hit.title}</div>
                    <div className="pb-tile__meta">
                      {[hit.year, "From BGG"].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                </div>
              </Tile>
            ))}

            {typed.length > 0 && (
              <Tile
                as="button"
                type="button"
                title={`Use “${typed}”`}
                meta="Typed in — no lookup needed"
                edge="ochre"
                onClick={() => setChoice({ kind: "manual", title: typed })}
              />
            )}

            {typed.length === 0 && localHits.length === 0 && (
              <p className="pb-hint">
                Nothing played yet. Type any title — it doesn't have to be on BGG.
              </p>
            )}

            {bgg === "searching" && <div className="pb-code__hint">Looking up the title…</div>}

            {/* Credit and caveat in one line, because they are the same fact:
                the list is a snapshot of BoardGameGeek's public ranking table,
                which is base games only. Anything it misses is typed in, and
                the row above does exactly that. Text only — no logo, since a
                bundled snapshot is not a BGG partnership and must not look like
                one. */}
            {typed.length > 0 && (
              <p className="pb-hint">
                Titles from BoardGameGeek's public game rankings. Expansions and very new games
                often aren't listed — typing the title in always works.
              </p>
            )}
          </>
        )}
      </div>

      {recall && (
        <>
          <hr className="pb-rule" />
          {recallApplied ? (
            <div className="pb-notice" data-tone="ok">
              <span className="pb-notice__strong">Set up</span>
              <span>Same players and questions as {shortDate(recall.played_at)}.</span>
            </div>
          ) : (
            <Tile
              as="button"
              type="button"
              title="Same as last time"
              meta={`${recall.profile_ids.length} playing · ${recall.aspect_ids.length} questions · ${shortDate(recall.played_at)}`}
              edge="spice"
              onClick={applyRecall}
            />
          )}
        </>
      )}

      <hr className="pb-rule" />

      <div className="pb-section">
        <div className="pb-label">Who played</div>
        <div className="pb-chips">
          {roster.map((profile) => {
            const on = chosenProfiles.includes(profile.id);
            return (
              <button
                key={profile.id}
                type="button"
                className="pb-chip"
                data-on={on ? "true" : "false"}
                aria-pressed={on}
                onClick={() => toggleProfile(profile.id)}
              >
                <span
                  className="pb-swatch"
                  style={{ background: profile.colour }}
                  aria-hidden="true"
                />
                <span>{profile.name}</span>
                {profile.is_guest === 1 && <span className="pb-chip__meta">guest</span>}
                {standingOf.has(profile.id) && (
                  // The reasoning behind the order, shown rather than implied —
                  // the top of this list is a claim about your group, and a
                  // wrong one is easier to correct when you can see why.
                  <span className="pb-chip__meta">
                    {standingOf.get(profile.id)!.plays} play
                    {standingOf.get(profile.id)!.plays === 1 ? "" : "s"}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <div className="pb-actions">
          <input
            className="pb-input"
            type="text"
            value={guestName}
            placeholder="Someone new at the table"
            aria-label="Add someone new"
            style={{ flex: "1 1 auto" }}
            onChange={(event) => setGuestName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                void addGuest();
              }
            }}
          />
          <button
            type="button"
            className="pb-btn"
            data-variant="quiet"
            style={{ flex: "0 0 auto" }}
            disabled={guestName.trim().length === 0}
            onClick={() => void addGuest()}
          >
            Add
          </button>
        </div>
      </div>

      <hr className="pb-rule" />

      <div className="pb-section">
        <div className="pb-label">What to ask about</div>
        <div className="pb-chips">
          {aspects.map((aspect) => {
            const on = chosenAspects.has(aspect.id);
            const extra = followUps.get(aspect.id)?.length ?? 0;
            // A once-per-game aspect that this game already has an answer for
            // says so, and says when, instead of repeating the rule it is an
            // instance of. The chip is still here and still tappable: the group
            // may well want to re-answer how a box looks after a reprint.
            const settled = answeredStable.get(aspect.id);
            return (
              <button
                key={aspect.id}
                type="button"
                className="pb-chip"
                data-on={on ? "true" : "false"}
                aria-pressed={on}
                onClick={() => toggleAspect(aspect.id)}
                title={
                  extra > 0
                    ? `Follow-ups: ${followUps
                        .get(aspect.id)!
                        .map((sq) => sq.name)
                        .join(", ")}`
                    : undefined
                }
              >
                <span>{aspect.name}</span>
                {aspect.stable === 1 && (
                  <span className="pb-chip__meta">
                    {settled === undefined ? "once per game" : `answered ${shortDate(settled)}`}
                  </span>
                )}
                {extra > 0 && (
                  <span className="pb-chip__meta">
                    +{extra} follow-up{extra === 1 ? "" : "s"}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <p className="pb-hint">{aspectHint(aspects, followUps)}</p>
        {settledOffered > 0 && (
          <p className="pb-hint">
            {settledOffered === 1 ? "One question is" : `${settledOffered} questions are`} only
            asked once per game, and this game has already answered{" "}
            {settledOffered === 1 ? "it" : "them"}, so{" "}
            {settledOffered === 1 ? "it starts" : "they start"} switched off. Tap to ask again.
          </p>
        )}
      </div>

      <hr className="pb-rule" />

      <div className="pb-section">
        <div className="pb-label">How you'll rate</div>
        {MODES.map((option) => (
          <Tile
            key={option.value}
            as="button"
            type="button"
            title={option.title}
            edge={mode === option.value ? "spice" : "muted"}
            aria-pressed={mode === option.value}
            onClick={() => setMode(option.value)}
          >
            <p className="pb-hint">{option.blurb}</p>
          </Tile>
        ))}
      </div>

      {refusal && (
        // Directly above the button that was tapped, so the answer to "did that
        // do anything?" is where the thumb already is.
        <div className="pb-notice" data-tone="alert" role="alert">
          <span className="pb-notice__strong">{refusalText(refusal).title}</span>
          <span>{refusalText(refusal).body}</span>
        </div>
      )}

      <div className="pb-actions">
        <button
          type="button"
          className="pb-btn"
          data-variant="primary"
          disabled={!canStart}
          onClick={() => void start()}
        >
          {busy ? "Setting up" : mode === "SHARED_DEVICE" ? "Start rating" : "Get the join code"}
        </button>
      </div>

      {!canStart && !busy && (
        <p className="pb-hint">
          {choice ? "Pick at least one person who played." : "Pick or type the game first."}
        </p>
      )}
    </div>
  );
}
